import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { PlanVariant, Trip } from "../../../types";
import { LOAD_MULTIPLE_TRIP_CONFLICT_QUERY } from "globals/graphql/trip.graphql";
import { useOperator } from "globals/hooks";

/**
 * Custom hook to check for trip vehicle conflicts
 * @param trips List of trips to check for conflicts
 * @param includeReturnTrips Whether to include return trips in the conflict check
 * @returns tripConflicts map and loading state
 * 
 * ex.
 * Map([
    [
        "VHJpcDpjMTY4Mzg2Ni0xMGEzLTExZjAtOTQyYy1jZmRkYjI2NjA0YTc=",
        false
    ],
    [
        "VHJpcDpjMTZhYmFhMC0xMGEzLTExZjAtOTQyYy1mMzdkY2ZmNjdhMTk=",
        true
    ]
])
 */
export function useTripConflicts(trips: Trip[], includeReturnTrips = true): {
  tripConflicts: Map<string, boolean>;
  loading: boolean;
} {
  const operator = useOperator();
  const isProOrAbove =
    operator?.plan === PlanVariant.Pro || operator?.plan === PlanVariant.Vip;

  // Create pairs of tripId and vehicleId for conflict checking, tracking their source
  const { tripVehiclePairs } = useMemo(() => {
    const pairs = [];
    
    trips.forEach((trip, tripIndex) => {
      if (trip.routes[0]?.vehicle?.id) {
        pairs.push({
          tripId: trip.id,
          vehicleId: trip.routes[0].vehicle.id,
        });
      }
      
      // Add return trip if it exists and has a vehicle
      if (includeReturnTrips && trip.returnTrip?.routes[0]?.vehicle?.id) {
        pairs.push({
          tripId: trip.returnTrip.id,
          vehicleId: trip.returnTrip.routes[0].vehicle.id,
        });
      }
    });
    
    return { tripVehiclePairs: pairs };
  }, [trips, includeReturnTrips]);

  // Query for conflicts
  const { data, loading } = useQuery(LOAD_MULTIPLE_TRIP_CONFLICT_QUERY, {
    variables: {
      tripVehiclePairs,
    },
    skip: tripVehiclePairs.length === 0 || !isProOrAbove,
  });

  const tripConflicts = useMemo(() => {
    const conflicts = new Map<string, boolean>();
    
    if (data?.loadMultipleTripConflict) {
      data.loadMultipleTripConflict.forEach(({ tripId, hasConflict }) => {
        conflicts.set(tripId, hasConflict);
      });
    }
    
    return conflicts;
  }, [data]);

  return { tripConflicts, loading };
} 